/**
 * Monkey Worlds — the game's score: note data + the timing math, shared by the runtime and Vitest.
 *
 * ─── ORIGINALITY (spec AC-18) ────────────────────────────────────────────────────────────────
 * The motif below is an ORIGINAL composition authored for this game. It must not be transcribed
 * from, quoted from, or "arranged after" any existing work — no other game's theme, no pop or film
 * melody. The test suite checks that notes stay inside the declared scale; it CANNOT check
 * originality. That guarantee is procedural: author it, never copy it. If you find yourself
 * humming something else while editing the table, you have drifted — revert.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 *
 * Musical frame: A major pentatonic, root A3 (MIDI 57 = 220 Hz), scale degrees {0,2,4,7,9}
 * semitones. A pentatonic set has no semitone clashes, so every melody note is consonant over
 * every chord in the loop — that is what makes the tune hard to break, and it is machine-checkable
 * (T-35). Harmony is a four-bar I–vi–IV–V (`BAR_ROOTS`), the grid is 16th notes, and the motif is
 * 64 steps = 4 bars.
 *
 * Shape: bar 0 calls (a 16th "chatter" then an octave leap), bar 1 answers and leaves it open,
 * bar 2 repeats the call a pentatonic third higher — the hook — and bar 3 resolves to the tonic
 * and rests, so the loop breathes instead of nagging.
 *
 * ONE tune, four dresses: `ARRANGEMENTS` re-voices the same motif per world (transpose, tempo,
 * waveform, swing, bass pattern). They are keyed by NUMERIC WORLD INDEX, never by the internal
 * sport id — serialising a sport id into the runtime HTML would trip the copy guard (T-8).
 *
 * Embedding constraints (same contract as monkeyWorldsScoring.ts): the exported timing functions
 * are embedded into the runtime via `getMonkeyWorldsMusicSource()`, so their bodies must be plain
 * ES5 function declarations (`var`, no arrow functions) that capture nothing from module scope
 * The embedded functions take every tuning value as an argument — see getMonkeyWorldsMusicSource
 * for why they must not touch module scope.
 */

export interface MonkeyWorldsNote {
  /** Onset, in 16th-note steps from the start of the loop. */
  t: number;
  /** Pitch, in semitones from the track root (may be negative or above an octave). */
  n: number;
  /** Duration in steps. */
  d: number;
}

export type MonkeyWorldsWave = 'square' | 'triangle' | 'sawtooth' | 'sine';
export type MonkeyWorldsBassPatternId = 'march' | 'bounce' | 'shuffle' | 'stomp' | 'sparse';

export interface MonkeyWorldsArrangement {
  /** Semitones to shift the whole arrangement. */
  transpose: number;
  bpm: number;
  melodyWave: MonkeyWorldsWave;
  bassWave: MonkeyWorldsWave;
  /** 0 = straight; a fraction of a step by which odd 16ths are delayed. */
  swing: number;
  bass: MonkeyWorldsBassPatternId;
  /** Semitones to raise the melody only (12 = an octave up). */
  melodyOctave: number;
  /** Peak gains — both must stay below the 0.08 SFX peak (T-35). */
  melodyGain: number;
  bassGain: number;
}

export interface MonkeyWorldsTrack {
  /** Inclusive bar slice of the motif, or null when `notes` carries a bespoke stinger. */
  bars: [number, number] | null;
  notes: readonly MonkeyWorldsNote[] | null;
  loop: boolean;
  /** Overrides layered over the world arrangement; null means "inherit". */
  transpose: number | null;
  bpm: number | null;
  tempoScale: number;
  melodyWave: MonkeyWorldsWave | null;
  bass: MonkeyWorldsBassPatternId | null;
  /** Pins every bar to one root — an F#-ish pedal recolours the same notes darker. */
  pedalRoot: number | null;
  gainScale: number;
  /** Seconds to wait before the first note (keeps a stinger off a colliding sound effect). */
  delaySec: number;
}

export type MonkeyWorldsSceneId = 'map' | 'play' | 'shootout' | 'clear' | 'failed' | 'worldComplete';

/** A3. The runtime gets this injected as a global because the embedded fns close over it. */
export const MONKEY_WORLDS_ROOT_MIDI = 57;
export const MONKEY_WORLDS_STEPS_PER_BAR = 16;
/** The pentatonic set, as semitones within an octave. Every note must be a member (T-35). */
export const MONKEY_WORLDS_SCALE: readonly number[] = [0, 2, 4, 7, 9];

/** I – vi – IV – V in A: A, F#, D, E. Bass renders these an octave down. */
export const MONKEY_WORLDS_BAR_ROOTS: readonly number[] = [0, -3, -7, -5];

/**
 * The motif — 4 bars, monophonic, every note pentatonic.
 * Bars: 0 = call · 1 = open answer · 2 = the call sequenced a third up (the hook) · 3 = resolve.
 */
export const MONKEY_WORLDS_MOTIF: readonly MonkeyWorldsNote[] = [
  // bar 0 — the call: chatter, then a leap to the octave.
  { t: 0, n: 7, d: 2 },
  { t: 2, n: 7, d: 1 },
  { t: 3, n: 9, d: 1 },
  { t: 4, n: 12, d: 4 },
  { t: 8, n: 9, d: 2 },
  { t: 10, n: 7, d: 2 },
  { t: 12, n: 4, d: 3 },
  { t: 15, n: 2, d: 1 },
  // bar 1 — the answer, rising rather than resolving.
  { t: 16, n: 4, d: 2 },
  { t: 18, n: 4, d: 1 },
  { t: 19, n: 2, d: 1 },
  { t: 20, n: 7, d: 4 },
  { t: 24, n: 9, d: 2 },
  { t: 26, n: 12, d: 2 },
  { t: 28, n: 9, d: 3 },
  // bar 2 — the hook: bar 0's rhythm, a pentatonic third higher.
  { t: 32, n: 9, d: 2 },
  { t: 34, n: 9, d: 1 },
  { t: 35, n: 12, d: 1 },
  { t: 36, n: 16, d: 4 },
  { t: 40, n: 14, d: 2 },
  { t: 42, n: 12, d: 2 },
  { t: 44, n: 9, d: 3 },
  { t: 47, n: 7, d: 1 },
  // bar 3 — home, then a breath before the loop repeats.
  { t: 48, n: 7, d: 2 },
  { t: 50, n: 4, d: 2 },
  { t: 52, n: 2, d: 2 },
  { t: 54, n: 0, d: 6 },
];

/**
 * Bass as an 8-slot (8th-note) interval pattern per bar, played over that bar's root, an octave
 * down. Entries are limited to root/fifth/octave so every pattern is consonant with every bar by
 * construction — which is what makes four arrangements nearly free. `null` is a rest.
 */
export const MONKEY_WORLDS_BASS_PATTERNS: Readonly<
  Record<MonkeyWorldsBassPatternId, readonly (number | null)[]>
> = {
  march: [0, null, 0, null, 7, null, 0, null],
  bounce: [0, 12, 7, 12, 0, 12, 7, 12],
  shuffle: [0, null, 7, null, 0, 12, 7, null],
  stomp: [0, 0, null, 0, 7, 7, null, 7],
  sparse: [0, null, null, null, 7, null, null, null],
};

/**
 * One tune, four dresses — index-aligned with MONKEY_WORLDS.
 *  0 Football — square lead over an on-beat march: the terraces reading.
 *  1 Hoops    — fastest, brightest, bass ping-ponging octaves on every 8th: it dribbles.
 *  2 Diamond  — triangle waves, swung 16ths, melody an octave up, slowest: sunlit and loose.
 *  3 Gridiron — lowest, sawtooth lead (harsh, so its gain is trimmed), double-hit bass: heavy.
 */
export const MONKEY_WORLDS_ARRANGEMENTS: readonly MonkeyWorldsArrangement[] = [
  { transpose: 0, bpm: 132, melodyWave: 'square', bassWave: 'triangle', swing: 0, bass: 'march', melodyOctave: 0, melodyGain: 0.03, bassGain: 0.026 },
  { transpose: 3, bpm: 144, melodyWave: 'square', bassWave: 'square', swing: 0, bass: 'bounce', melodyOctave: 0, melodyGain: 0.028, bassGain: 0.022 },
  { transpose: 5, bpm: 120, melodyWave: 'triangle', bassWave: 'triangle', swing: 0.18, bass: 'shuffle', melodyOctave: 12, melodyGain: 0.032, bassGain: 0.024 },
  { transpose: -2, bpm: 126, melodyWave: 'sawtooth', bassWave: 'square', swing: 0, bass: 'stomp', melodyOctave: 0, melodyGain: 0.022, bassGain: 0.028 },
];

/** Rising tonic arpeggio through the motif's own high C#. */
const CLEAR_STINGER: readonly MonkeyWorldsNote[] = [
  { t: 0, n: 0, d: 2 },
  { t: 2, n: 4, d: 2 },
  { t: 4, n: 7, d: 2 },
  { t: 6, n: 12, d: 2 },
  { t: 8, n: 16, d: 4 },
];

/** The clear stinger, carried further and landing on a held octave. */
const WORLD_COMPLETE_STINGER: readonly MonkeyWorldsNote[] = [
  { t: 0, n: 0, d: 2 },
  { t: 2, n: 4, d: 2 },
  { t: 4, n: 7, d: 2 },
  { t: 6, n: 12, d: 2 },
  { t: 8, n: 16, d: 4 },
  { t: 12, n: 19, d: 2 },
  { t: 14, n: 16, d: 2 },
  { t: 16, n: 12, d: 8 },
];

/** A short fall. Delayed so it does not land on top of the existing fail sound effect. */
const FAILED_STINGER: readonly MonkeyWorldsNote[] = [
  { t: 0, n: 0, d: 3 },
  { t: 3, n: -3, d: 3 },
  { t: 6, n: -5, d: 3 },
  { t: 9, n: -12, d: 6 },
];

function track(partial: Partial<MonkeyWorldsTrack>): MonkeyWorldsTrack {
  return {
    bars: null,
    notes: null,
    loop: false,
    transpose: null,
    bpm: null,
    tempoScale: 1,
    melodyWave: null,
    bass: null,
    pedalRoot: null,
    gainScale: 1,
    delaySec: 0,
    ...partial,
  };
}

export const MONKEY_WORLDS_SCENE_TRACKS: Readonly<Record<MonkeyWorldsSceneId, MonkeyWorldsTrack>> = {
  // A calm lobby reading of the same tune.
  map: track({ bars: [0, 3], loop: true, transpose: 0, bpm: 104, melodyWave: 'triangle', bass: 'sparse', gainScale: 0.8 }),
  // The world's own arrangement, unmodified.
  play: track({ bars: [0, 3], loop: true }),
  // Tension: the hook and its resolve only, pinned to one darker root and pushed slightly faster.
  shootout: track({ bars: [2, 3], loop: true, tempoScale: 1.12, melodyWave: 'square', bass: 'stomp', pedalRoot: -3 }),
  clear: track({ notes: CLEAR_STINGER, bpm: 160, melodyWave: 'square' }),
  worldComplete: track({ notes: WORLD_COMPLETE_STINGER, bpm: 150, melodyWave: 'square', bass: 'march' }),
  failed: track({ notes: FAILED_STINGER, bpm: 100, melodyWave: 'sawtooth', delaySec: 0.18 }),
};

/** Everything the runtime needs, in one JSON-serialisable bundle. */
export const MONKEY_WORLDS_MUSIC_DATA = {
  stepsPerBar: MONKEY_WORLDS_STEPS_PER_BAR,
  barRoots: MONKEY_WORLDS_BAR_ROOTS,
  motif: MONKEY_WORLDS_MOTIF,
  bassPatterns: MONKEY_WORLDS_BASS_PATTERNS,
  arrangements: MONKEY_WORLDS_ARRANGEMENTS,
  scenes: MONKEY_WORLDS_SCENE_TRACKS,
} as const;

// ---------------------------------------------------------------------------------------------
// Pure timing math. Embedded into the runtime verbatim — keep bodies ES5 (see the header).
// ---------------------------------------------------------------------------------------------

/** Equal-temperament frequency for a pitch given in semitones from the root. */
export function monkeyWorldsNoteHz(
  semitoneFromRoot: number,
  transpose: number,
  rootMidi: number
): number {
  var midi = rootMidi + semitoneFromRoot + transpose;
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** Seconds per 16th-note step at a given tempo. */
export function monkeyWorldsStepSec(bpm: number): number {
  if (!(bpm > 0)) return 0.125;
  return 60 / bpm / 4;
}

/**
 * Absolute time of a step. Swing delays odd 16ths by a fraction of a step, which is what gives the
 * Diamond arrangement its loose feel; it never reorders steps.
 */
export function monkeyWorldsStepTime(
  step: number,
  startedAt: number,
  bpm: number,
  swing: number
): number {
  // inlined rather than calling stepSec: a cross-call would break once minified
  var sec = bpm > 0 ? 60 / bpm / 4 : 0.125;
  var sw = swing > 0 ? swing : 0;
  if (sw > 0.45) sw = 0.45;
  var offset = step % 2 === 1 ? sec * sw : 0;
  return startedAt + step * sec + offset;
}

/** The notes of an inclusive bar slice, rebased so the slice starts at step 0. */
export function monkeyWorldsMotifSlice(bars: [number, number]): MonkeyWorldsNote[] {
  var from = bars[0] * MONKEY_WORLDS_STEPS_PER_BAR;
  var to = (bars[1] + 1) * MONKEY_WORLDS_STEPS_PER_BAR;
  var out: MonkeyWorldsNote[] = [];
  for (var i = 0; i < MONKEY_WORLDS_MOTIF.length; i++) {
    var note = MONKEY_WORLDS_MOTIF[i];
    if (note.t >= from && note.t < to) out.push({ t: note.t - from, n: note.n, d: note.d });
  }
  return out;
}

/**
 * Canonical runtime name → function. The runtime calls these EXACT names, so each is bound by name
 * rather than relying on `Function.name`, which minification mangles (see monkeyWorldsScoring.ts).
 */
const EMBEDDED_FNS = [
  ['noteHz', monkeyWorldsNoteHz],
  ['stepSec', monkeyWorldsStepSec],
  ['stepTime', monkeyWorldsStepTime],
] as const;

export const MONKEY_WORLDS_MUSIC_EMBEDDED_FN_NAMES: readonly string[] = EMBEDDED_FNS.map(
  ([name]) => name
);

/**
 * Source for the runtime's <script>, each function bound to its canonical name.
 *
 * Every function here MUST be a leaf: no reference to a module-scope const, and no call to another
 * module function. Minification renames those bindings, and the runtime only defines the canonical
 * names — so a reference that resolves in-module throws `<mangled> is not defined` on every frame
 * in a release build. `leafInvariant` in the test suite enforces this by running the source with
 * nothing but Math/JSON in scope.
 */
export function getMonkeyWorldsMusicSource(): string {
  const lines: string[] = [];
  for (const [name, fn] of EMBEDDED_FNS) {
    lines.push(`var ${name} = ${fn.toString()};`);
  }
  return lines.join('\n');
}
